import { useEffect, useRef, useState } from 'react';

/**
 * Commits `value` at most once per `intervalMs` while `enabled`.
 * Always flushes immediately when `enabled` becomes false (e.g. stream end).
 */
export function useThrottledContent(
  value: string,
  enabled: boolean,
  intervalMs = 140
): string {
  const [committed, setCommitted] = useState(value);
  const latestRef = useRef(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommitRef = useRef(0);

  useEffect(() => {
    latestRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setCommitted(value);
      lastCommitRef.current = Date.now();
      return;
    }

    const now = Date.now();
    const elapsed = now - lastCommitRef.current;

    if (elapsed >= intervalMs) {
      setCommitted(value);
      lastCommitRef.current = now;
      return;
    }

    if (timeoutRef.current !== null) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setCommitted(latestRef.current);
      lastCommitRef.current = Date.now();
    }, intervalMs - elapsed);
  }, [enabled, intervalMs, value]);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  return enabled ? committed : value;
}
